import { useRef, type ChangeEvent } from 'react';

import { InputAdornment, TextField, type TextFieldProps } from '@mui/material';
import { usePhoneInput } from 'react-international-phone';

import { SUPPORTED_COUNTRY_DATA } from './config';
import { CountrySelect } from './country-select';

export type PhoneTextFieldProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
  value?: string;
  onChange: (value: string) => void;
  showCountrySelect?: boolean;
};

export const PhoneTextField = (props: PhoneTextFieldProps) => {
  const {
    value,
    onChange,
    disabled,
    showCountrySelect = false,
    slotProps,
    ...rest
  } = props;

  // `forceDialCode` makes the library emit onChange with the prefilled dial code
  // on mount (and again under StrictMode's double-invoke). Propagate to the form
  // only after the user actually types, so an empty field stays empty in the form
  // state and shows no premature validation error.
  const hasUserTypedRef = useRef(false);

  const phoneInput = usePhoneInput({
    defaultCountry: 'ua',
    value,
    countries: SUPPORTED_COUNTRY_DATA,
    forceDialCode: true,
    onChange: (data) => {
      if (!hasUserTypedRef.current) return;

      onChange(data.phone);
    },
  });

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    hasUserTypedRef.current = true;
    phoneInput.handlePhoneValueChange(event);
  };

  return (
    <TextField
      {...rest}
      disabled={disabled}
      fullWidth
      type="tel"
      value={phoneInput.inputValue}
      onChange={handleInputChange}
      inputRef={phoneInput.inputRef}
      slotProps={{
        input: {
          startAdornment: showCountrySelect ? (
            <InputAdornment position="start">
              <CountrySelect
                selectedIso2={phoneInput.country.iso2}
                onSelect={(iso2) => phoneInput.setCountry(iso2, { focusOnInput: true })}
                disabled={disabled}
              />
            </InputAdornment>
          ) : undefined,
        },
        ...slotProps,
      }}
    />
  );
};
